import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardApi } from "@/services/api";

interface KPIWidget {
  label: string;
  value: string | number;
  subValue?: string;
  icon: string;
  color: string;
  bg: string;
  iconColor: string;
  change: string;
  up: boolean;
  path: string;
  query?: string;
  pulse?: boolean;
}

interface DashboardStats {
  totalJobs: number;
  activeJobs: number;
  pendingJobs: number;
  completedJobs: number;
  deliveredJobs: number;
  monthlyRevenue: number;
  employeeUtilization: number;
  upcomingDeadlines: number;
  overloadedEmployees?: number;
  unreadNotifications?: number;
}

export default function LiveKPIWidgets() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const data = await dashboardApi.getStats();
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl p-4 border border-amber-100/50 animate-pulse">
            <div className="w-10 h-10 rounded-xl bg-amber-50 mb-3" />
            <div className="h-7 bg-amber-50 rounded w-12 mb-1" />
            <div className="h-3 bg-amber-50 rounded w-20" />
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="grid grid-cols-1">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
          <p className="text-sm font-medium" style={{ color: "#92400e" }}>
            Dashboard stats unavailable — check backend connection
          </p>
        </div>
      </div>
    );
  }

  const overloadedEmps = stats.overloadedEmployees ?? 0;
  const unreadNotifs = stats.unreadNotifications ?? 0;
  const revenueLow = stats.monthlyRevenue < 100000;

  const widgets: KPIWidget[] = [
    {
      label: "Active Jobs",
      value: stats.activeJobs,
      subValue: `${stats.pendingJobs} pending`,
      icon: "ri-loader-4-line",
      color: "#d97706",
      bg: "#fffbeb",
      iconColor: "#f59e0b",
      change: "+2 today",
      up: true,
      path: "/job-cards",
      query: "status=In Progress",
      pulse: true,
    },
    {
      label: "Completed",
      value: stats.completedJobs,
      subValue: `${stats.totalJobs} total`,
      icon: "ri-checkbox-circle-line",
      color: "#b45309",
      bg: "#fef3c7",
      iconColor: "#d97706",
      change: "+18%",
      up: true,
      path: "/job-cards",
      query: "status=Completed",
    },
    {
      label: "Monthly Revenue",
      value: `₹${(stats.monthlyRevenue / 100000).toFixed(1)}L`,
      subValue: revenueLow ? "No invoices yet" : "₹24.8L target",
      icon: "ri-money-rupee-circle-line",
      color: revenueLow ? "#92400e" : "#d97706",
      bg: revenueLow ? "#fef2f2" : "#fffbeb",
      iconColor: revenueLow ? "#ef4444" : "#f59e0b",
      change: revenueLow ? "Create now" : "+16%",
      up: !revenueLow,
      path: "/billing",
      query: revenueLow ? undefined : "tab=Invoice",
      pulse: revenueLow,
    },
    {
      label: "Team Utilization",
      value: `${stats.employeeUtilization}%`,
      subValue: `${overloadedEmps} overloaded`,
      icon: "ri-team-line",
      color: overloadedEmps > 2 ? "#ef4444" : "#b45309",
      bg: overloadedEmps > 2 ? "#fef2f2" : "#fef3c7",
      iconColor: overloadedEmps > 2 ? "#ef4444" : "#d97706",
      change: overloadedEmps > 2 ? "High load" : "Optimal",
      up: overloadedEmps <= 2,
      path: "/employees",
      query: overloadedEmps > 0 ? "status=active" : undefined,
    },
    {
      label: "Deadlines",
      value: stats.upcomingDeadlines,
      subValue: "Next 7 days",
      icon: "ri-alarm-warning-line",
      color: "#ef4444",
      bg: "#fef2f2",
      iconColor: "#ef4444",
      change: "2 critical",
      up: false,
      path: "/job-cards",
      query: "filter=overdue",
      pulse: true,
    },
    {
      label: "Unread Alerts",
      value: unreadNotifs,
      subValue: "Notifications",
      icon: "ri-notification-3-line",
      color: "#92400e",
      bg: "#fef3c7",
      iconColor: "#d97706",
      change: `${unreadNotifs} new`,
      up: false,
      path: "/notifications",
      pulse: unreadNotifs > 0,
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
      {widgets.map((w) => (
        <div
          key={w.label}
          onClick={() => navigate(w.query ? `${w.path}?${w.query}` : w.path)}
          className="bg-white rounded-xl p-3 md:p-4 border border-amber-100/60 hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer group relative overflow-hidden"
        >
          {/* Subtle amber gradient top bar */}
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ background: "linear-gradient(90deg, #f59e0b, #d97706)" }} />

          {w.pulse && (
            <span className="absolute top-3 right-3 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: w.iconColor }} />
              <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: w.iconColor }} />
            </span>
          )}

          <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center mb-2 md:mb-3" style={{ background: w.bg }}>
            <i className={`${w.icon} text-base md:text-lg`} style={{ color: w.iconColor }} />
          </div>

          <p className="text-xl md:text-2xl font-bold text-gray-900 group-hover:transition-colors" style={{ color: undefined }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = w.color; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = ""; }}
          >
            {w.value}
          </p>
          <p className="text-xs font-medium text-gray-700 mt-0.5 leading-tight">{w.label}</p>
          {w.subValue && <p className="text-xs text-gray-400 mt-0.5 hidden sm:block">{w.subValue}</p>}

          {/* Create Invoice quick action for low revenue */}
          {w.label === "Monthly Revenue" && revenueLow && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/billing/invoice/new");
              }}
              className="mt-2 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-all"
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-add-line text-xs" />
              </span>
              Create Invoice
            </button>
          )}

          <div className="mt-2 flex items-center gap-1">
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ color: w.color, background: w.bg }}>
              {w.up ? "↑" : "↓"} {w.change}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}